const CACHE_NAME = "atlas-image-cache";
import Bugsnag from "@bugsnag/js";

interface RetryOptions {
  maxRetries: number;
  initialDelay: number;
  timeout?: number;
}

const DEFAULT_TIMEOUT = 30000; // 30 seconds

/**
 * Checks if an error is a network error that should be retried
 */
function isRetryableError(error: any): boolean {
  if (!(error instanceof Error)) return false;
  // Network errors (no response received)
  if (error instanceof TypeError && error.message.includes("fetch")) {
    return true;
  }
  // Timeout errors
  if (error.name === "AbortError") {
    return true;
  }
  return false;
}

/**
 * Delay execution for a specified time
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Fetches a URL with retry logic and exponential backoff
 */
async function fetchWithRetry(
  url: string,
  options: RetryOptions,
  attempt = 0
): Promise<Response> {
  const controller = new AbortController();
  const timeoutMs = options.timeout || DEFAULT_TIMEOUT;
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    const response = await fetch(url, { signal: controller.signal });

    if (!response.ok) {
      // Retry on specific HTTP status codes
      if (
        attempt < options.maxRetries &&
        [408, 429, 502, 503, 504, 400].includes(response.status)
      ) {
        const delayMs = options.initialDelay * Math.pow(2, attempt);
        await delay(delayMs);
        return fetchWithRetry(url, options, attempt + 1);
      }
      throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
    }

    return response;
  } catch (error) {
    if (attempt < options.maxRetries && isRetryableError(error)) {
      const delayMs = options.initialDelay * Math.pow(2, attempt);
      await delay(delayMs);
      return fetchWithRetry(url, options, attempt + 1);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Returns true when the error is a SecurityError thrown by the CacheStorage API.
 * This happens in private/incognito browsing mode or when the browser's security
 * policy blocks access to CacheStorage, and is not an actionable application error.
 */
function isCacheStorageSecurityError(error: unknown): boolean {
  return (
    error instanceof DOMException &&
    error.name === "SecurityError" &&
    error.message.indexOf("CacheStorage") !== -1
  );
}

/**
 * Fetches and caches an image by URL. Returns the cached response if available.
 *
 * @param url - The URL of the image to fetch and cache.
 * @returns The response object from the cache or network.
 * @throws Error if the network response is not OK after retries.
 */
async function cacheImage(url: string): Promise<Response | undefined> {
  if (!("caches" in window) || !url) return;
  try {
    const cache = await caches.open(CACHE_NAME);

    const cached = await cache.match(url);
    if (cached) return cached;

    const retryOptions: RetryOptions = {
      maxRetries: 2, // Total of 3 attempts: 1 initial + 2 retries
      initialDelay: 1000,
      timeout: DEFAULT_TIMEOUT
    };

    const response = await fetchWithRetry(url, retryOptions);

    // Cache.put() failures (NetworkError, QuotaExceededError, etc.) are not
    // actionable — the image was already fetched and returned to the caller.
    cache.put(url, response.clone()).catch(() => {});

    return response;
  } catch (error) {
    if (isCacheStorageSecurityError(error)) {
      // CacheStorage access is blocked by the browser's security policy
      // (e.g., private/incognito browsing mode). This is not an app error.
      return;
    }
    reportToBugsnag(error);
  }
}

export async function loadImage(url: string) {
  try {
    const response = await cacheImage(url);
    if (response) {
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    }
  } catch (error) {
    reportToBugsnag(error);
  }
}

function reportToBugsnag(error: unknown) {
  if (error instanceof DOMException && error.name === "AbortError") {
    return;
  }
  if (error instanceof Error) {
    Bugsnag.notify(error);
  } else {
    const wrapped = new Error("Unknown error");
    Bugsnag.notify(wrapped);
  }
}
